// components/pages/SponsorPortal.js
// Sponsor self-service portal, secured by the magic-link ?token= in the URL. Lets a sponsor
// see their status, pay once approved, edit (re-approval), cancel/manage billing, and view
// their metrics (impressions, clicks, regions, next renewal).
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import Container from '../common/Container';
import { SectionTitle } from '../common/SharedStyles';
import Button from '../common/Button';
import ThemeToggle from '../layout/ThemeToggle';
import {
  getSponsorship,
  updateSponsorship,
  startCheckout,
  openBillingPortal,
} from '../../services/sponsorshipService';

const Section = styled.section`
  padding: 6rem 0;
  background-color: var(--background-primary);
`;
const NarrowContainer = styled(Container)`
  max-width: 720px;
`;
const Card = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 1.5rem;
  margin-top: 1.5rem;
`;
const StatusPill = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: capitalize;
  color: #fff;
  background: ${p =>
    p.$status === 'active' ? '#43a047'
    : p.$status === 'approved' ? '#0288d1'
    : p.$status === 'pending' ? '#f0b432'
    : p.$status === 'rejected' ? '#e53935'
    : p.$status === 'removed' ? '#757575'
    : '#757575'};
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-color);
  &:last-child { border-bottom: none; }
`;
const Label = styled.span`color: var(--text-secondary);`;
const Value = styled.span`color: var(--text-primary); font-weight: 600; text-align: right; word-break: break-word;`;
const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  @media (max-width: 576px) { grid-template-columns: 1fr; }
`;
const Metric = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
`;
const MetricNumber = styled.div`font-size: 1.8rem; font-weight: 800; color: var(--accent-primary);`;
const MetricLabel = styled.div`font-size: 0.8rem; color: var(--text-secondary);`;
const LogoImg = styled.img`
  max-width: 160px; max-height: 80px; object-fit: contain;
  background: var(--background-primary); border: 1px solid var(--border-color);
  border-radius: 6px; padding: 6px;
`;
const Input = styled.input`
  padding: 0.75rem; border: 1px solid var(--border-color); border-radius: 4px;
  background: var(--background-primary); color: var(--text-primary); width: 100%;
  box-sizing: border-box;
  &:focus { outline: none; border-color: var(--accent-primary); }
`;
const FormGroup = styled.div`display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 1rem;`;
const Banner = styled.div`
  margin-top: 1rem; padding: 0.75rem; border-radius: 8px; font-size: 0.9rem; text-align: center;
  &.error { background: rgba(229,57,53,0.12); color: #e53935; }
  &.success { background: rgba(67,160,71,0.12); color: #43a047; }
  &.info { background: rgba(3,169,244,0.12); color: #0288d1; }
`;
const Actions = styled.div`display: flex; gap: 0.75rem; flex-wrap: wrap; margin-top: 1rem;`;
const RegionList = styled.div`margin-top: 0.75rem;`;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

// A small "?" help badge that reveals a plain-language explanation. Shows on hover AND on
// click/tap (so it works for touch and for users who don't hover). Kept intentionally
// simple and readable for a non-technical audience.
function HelpTip({ text }) {
  const [pinned, setPinned] = useState(false);
  const [hover, setHover] = useState(false);
  const open = pinned || hover;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle', marginLeft: 5 }}>
      <button
        type="button"
        aria-label="What does this mean?"
        onClick={(e) => { e.stopPropagation(); setPinned(p => !p); }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: 16, height: 16, lineHeight: '16px', textAlign: 'center',
          borderRadius: '50%', border: '1px solid var(--border-color)',
          background: 'var(--background-primary)', color: 'var(--text-secondary)',
          fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0,
        }}
      >
        ?
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
            width: 220, background: 'var(--background-secondary)', color: 'var(--text-primary)',
            border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 12px',
            fontSize: 12.5, lineHeight: 1.45, fontWeight: 400, textAlign: 'left',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)', zIndex: 50, whiteSpace: 'normal',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

const HELP = {
  impressions: 'How many times your logo has been shown to Sivo users in the app. Think of it as how many people saw your sponsorship.',
  clicks: 'How many times a Sivo user tapped your logo to visit your website.',
  ctr: 'Click-through rate = clicks ÷ impressions, shown as a percentage. It tells you what share of the people who saw your logo actually clicked it. Higher is better.',
  regions: 'Where your clicks are coming from, by city — the locations with the most clicks are listed first.',
  renewal: 'The date your $100 / 30-day sponsorship renews automatically. You can cancel any time before this date.',
};

const SponsorPortal = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const justPaid = searchParams.get('paid') === '1';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState(justPaid ? { type: 'success', text: 'Payment complete! Your sponsorship is going live — this can take a moment.' } : null);
  const [busy, setBusy] = useState(false);

  // Edit form
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ companyName: '', contactName: '', destinationUrl: '' });
  const [editLogo, setEditLogo] = useState('');

  const load = useCallback(async () => {
    if (!token) { setError('Missing portal token. Please use the link from your email.'); setLoading(false); return; }
    setLoading(true);
    try {
      const result = await getSponsorship(token);
      setData(result);
      setEditData({
        companyName: result.companyName || '',
        contactName: result.contactName || '',
        destinationUrl: result.destinationUrl || '',
      });
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);

  const handlePay = async () => {
    setBusy(true); setBanner(null);
    try {
      const { url } = await startCheckout(token);
      window.location.href = url;
    } catch (err) {
      setBanner({ type: 'error', text: err.message });
      setBusy(false);
    }
  };

  const handleBilling = async () => {
    setBusy(true); setBanner(null);
    try {
      const { url } = await openBillingPortal(token);
      window.location.href = url;
    } catch (err) {
      setBanner({ type: 'error', text: err.message });
      setBusy(false);
    }
  };

  const handleEditLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setEditLogo(''); return; }
    if (file.size > 2 * 1024 * 1024) { setBanner({ type: 'error', text: 'Logo is too large (max 2 MB).' }); e.target.value = ''; return; }
    try { setEditLogo(await readFileAsDataURL(file)); } catch { setBanner({ type: 'error', text: 'Could not read that image.' }); }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setBusy(true); setBanner(null);
    try {
      const payload = { ...editData };
      if (editLogo) payload.logoData = editLogo;
      const result = await updateSponsorship(token, payload);
      setBanner({ type: 'success', text: result.message || 'Your changes were submitted for review.' });
      setEditing(false);
      setEditLogo('');
      await load();
    } catch (err) {
      setBanner({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Section><NarrowContainer><SectionTitle>Sponsor Portal</SectionTitle><p>Loading…</p></NarrowContainer></Section>;
  }
  if (error) {
    return (
      <Section><NarrowContainer>
        <SectionTitle>Sponsor Portal</SectionTitle>
        <Banner className="error">{error}</Banner>
        <p style={{ marginTop: '1rem' }}><Link to="/sponsor">Submit a new sponsorship</Link></p>
      </NarrowContainer></Section>
    );
  }

  const m = data.metrics || { impressions: 0, clicks: 0, regions: {}, daily: [] };
  const ctr = m.impressions > 0 ? ((m.clicks / m.impressions) * 100).toFixed(1) : '0.0';
  const regionEntries = Object.entries(m.regions || {}).sort((a, b) => b[1] - a[1]);

  return (
    <Section>
      <NarrowContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <SectionTitle style={{ margin: 0 }}>Sponsor Portal</SectionTitle>
          <span title="Toggle light / dark appearance"><ThemeToggle /></span>
        </div>

        <Card>
          <Row>
            <Label>Status</Label>
            <Value><StatusPill $status={data.status}>{data.status}</StatusPill></Value>
          </Row>
          {data.editStatus === 'pending' && (
            <Row><Label>Pending edit</Label><Value>Awaiting review — your current logo stays showcased.</Value></Row>
          )}
          <Row><Label>Company</Label><Value>{data.companyName}</Value></Row>
          <Row><Label>Destination</Label><Value>{data.destinationUrl}</Value></Row>
          <Row>
            <Label>Logo</Label>
            <Value>{data.logoUrl ? <LogoImg src={data.logoUrl} alt="Your logo" /> : '—'}</Value>
          </Row>
          {data.status === 'active' && (
            <Row><Label>Next renewal<HelpTip text={HELP.renewal} /></Label><Value>{formatDate(data.currentPeriodEnd)}</Value></Row>
          )}
          {data.status === 'rejected' && data.rejectionReason && (
            <Row><Label>Rejection reason</Label><Value>{data.rejectionReason}</Value></Row>
          )}
        </Card>

        {banner && <Banner className={banner.type}>{banner.text}</Banner>}

        {/* Status-specific guidance + actions */}
        {data.status === 'pending' && (
          <Card><p>Your submission is being reviewed. We'll email you as soon as it's approved.</p></Card>
        )}

        {data.status === 'approved' && (
          <Card>
            <p>You're approved! Start your $100 / 30-day subscription to go live in the app sidebar.</p>
            <Actions>
              <Button variant="primary" onClick={handlePay} disabled={busy}>{busy ? 'Please wait…' : 'Pay $100 / 30 days'}</Button>
            </Actions>
          </Card>
        )}

        {data.status === 'rejected' && (
          <Card><p>Your submission needs changes. Update it below and resubmit for review.</p></Card>
        )}

        {data.status === 'removed' && (
          <Card>
            <p>Your sponsorship has ended and your logo is no longer shown. You can restart any time.</p>
            <Actions>
              <Button variant="primary" onClick={handlePay} disabled={busy}>Restart sponsorship</Button>
            </Actions>
          </Card>
        )}

        {data.status === 'active' && (
          <Card>
            <p>Your sponsorship is live. Manage your card or cancel any time — your sponsorship is showcased until the end of the 30 days.</p>
            <Actions>
              <Button variant="secondary" onClick={handleBilling} disabled={busy}>Manage / Cancel subscription</Button>
            </Actions>
          </Card>
        )}

        {/* Metrics */}
        <Card>
          <h3 style={{ marginTop: 0 }}>Your metrics</h3>
          <MetricsGrid>
            <Metric><MetricNumber>{m.impressions}</MetricNumber><MetricLabel>Impressions<HelpTip text={HELP.impressions} /></MetricLabel></Metric>
            <Metric><MetricNumber>{m.clicks}</MetricNumber><MetricLabel>Clicks<HelpTip text={HELP.clicks} /></MetricLabel></Metric>
            <Metric><MetricNumber>{ctr}%</MetricNumber><MetricLabel>Click-through rate<HelpTip text={HELP.ctr} /></MetricLabel></Metric>
          </MetricsGrid>
          <RegionList>
            <Label>Top regions (by clicks)<HelpTip text={HELP.regions} /></Label>
            {regionEntries.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>No clicks yet.</p>
            ) : (
              regionEntries.slice(0, 8).map(([region, count], i) => {
                const pct = m.clicks > 0 ? Math.round((count / m.clicks) * 100) : 0;
                const barPct = regionEntries[0][1] > 0 ? Math.round((count / regionEntries[0][1]) * 100) : 0;
                return (
                  <div key={region} style={{ margin: '0.6rem 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: 3 }}>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{i + 1}. {region}</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{count} click{count === 1 ? '' : 's'} · {pct}%</span>
                    </div>
                    <div style={{ height: 8, background: 'var(--background-primary)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                      <div style={{ width: `${barPct}%`, height: '100%', background: 'var(--accent-primary)' }} />
                    </div>
                  </div>
                );
              })
            )}
          </RegionList>
        </Card>

        {/* Edit / resubmit */}
        {['approved', 'active', 'rejected', 'removed'].includes(data.status) && (
          <Card>
            {!editing ? (
              <Actions>
                <Button variant="outline" onClick={() => setEditing(true)}>
                  {data.status === 'rejected' ? 'Edit & resubmit' : 'Edit sponsorship'}
                </Button>
              </Actions>
            ) : (
              <form onSubmit={handleSaveEdit}>
                <h3 style={{ marginTop: 0 }}>Edit your sponsorship</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Edits go back to our team for review. Your billing cycle won't change, and your current
                  logo keeps showing until the edit is approved.
                </p>
                <FormGroup>
                  <label>Company Name</label>
                  <Input value={editData.companyName} onChange={e => setEditData(p => ({ ...p, companyName: e.target.value }))} />
                </FormGroup>
                <FormGroup>
                  <label>Contact Name</label>
                  <Input value={editData.contactName} onChange={e => setEditData(p => ({ ...p, contactName: e.target.value }))} />
                </FormGroup>
                <FormGroup>
                  <label>Destination URL</label>
                  <Input type="url" value={editData.destinationUrl} onChange={e => setEditData(p => ({ ...p, destinationUrl: e.target.value }))} />
                </FormGroup>
                <FormGroup>
                  <label>Replace logo (optional)</label>
                  <Input type="file" accept="image/*" onChange={handleEditLogo} />
                </FormGroup>
                <Actions>
                  <Button variant="primary" type="submit" disabled={busy}>{busy ? 'Submitting…' : 'Submit changes'}</Button>
                  <Button variant="outline" type="button" onClick={() => { setEditing(false); setEditLogo(''); }}>Cancel</Button>
                </Actions>
              </form>
            )}
          </Card>
        )}

        <Card>
          <h3 style={{ marginTop: 0 }}>Need help?</h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Questions about your sponsorship? Contact the Sivo team at{' '}
            <a href="mailto:support@signage-llc.com" style={{ color: 'var(--accent-primary)' }}>
              support@signage-llc.com
            </a>.
          </p>
        </Card>
      </NarrowContainer>
    </Section>
  );
};

export default SponsorPortal;
